import { useEffect, useRef } from 'react';
import classNames from 'classnames';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Select from 'components/partials/select/select';
import type { SelectOption } from 'components/partials/select/select';
import Button from 'components/partials/button/button';
import {
  useFeatureHintTarget,
  useIsFeatureHintActive,
} from 'components/shared/feature-hints';

import { useOpen } from 'hooks/use-open';
import { useClickOutside } from 'hooks/use-click-outside';

import Icons from 'assets/icons';

import {
  ALL_EXTEND_WITH_OPTIONS,
  EXTEND_WITH_PRODUCT_LABEL,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import { MONITORING_HINT_ID } from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-hints.config';
import type {
  CompareTo,
  ExtendWithOption,
  MonitoringDashboardParams,
  MonitoringFilterOptions,
  MonitoringShowToggles,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export const COMPARE_TO_OPTIONS: SelectOption<CompareTo>[] = [
  { label: 'Point in time', value: 'point-in-time' },
  { label: '7 days ago', value: 'last-7-days' },
  { label: '30 days ago', value: 'last-30-days' },
  { label: 'Prior year', value: 'prior-year' },
];

const SHOW_TOGGLE_LABEL: Record<keyof MonitoringShowToggles, string> = {
  client: 'My School',
  cohort: 'Cohort',
  vsCohort: 'My School vs Cohort (Delta)',
};

const CHIP_BASE =
  'rounded-md border font-semibold transition-colors focus-visible:ring-2 ring-offset-2 ring-navy-800';
const CHIP_SIZE = 'px-2.5 py-1 text-sm';
const CHIP_INACTIVE =
  'border-navy-200 bg-white text-navy-600 hover:border-navy-400 hover:bg-gray-50';
const CHIP_ACTIVE = 'border-navy-200 bg-navy-100 text-navy-800';
const FIELD_LABEL_CLASS = 'text-sm font-semibold text-gray-700 mb-1 mt-0';

export interface FiltersBarProps {
  filterOptions: MonitoringFilterOptions;
  params: Pick<
    MonitoringDashboardParams,
    'enteringClass' | 'compareTo' | 'cohort' | 'extend' | 'show'
  >;
  extendOptionsError?: unknown;
  onRetryExtendOptions?: () => void;
  onEnteringClassChange: (enteringClass: number) => void;
  onCompareToChange: (compareTo: CompareTo) => void;
  onCohortChange: (cohort: string) => void;
  onExtendToggle: (option: ExtendWithOption) => void;
  onShowToggle: (key: keyof MonitoringShowToggles, value: boolean) => void;
}

const FiltersBar = ({
  filterOptions,
  params,
  extendOptionsError,
  onRetryExtendOptions,
  onEnteringClassChange,
  onCompareToChange,
  onCohortChange,
  onExtendToggle,
  onShowToggle,
}: FiltersBarProps) => {
  const { isOpen, handleToggle, handleClose } = useOpen(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const isDisplayOptionsHintActive = useIsFeatureHintActive(
    MONITORING_HINT_ID.displayOptions
  );
  const displayOptionsButtonHint = useFeatureHintTarget(
    MONITORING_HINT_ID.displayOptions
  );
  const displayOptionsPanelHint = useFeatureHintTarget(
    MONITORING_HINT_ID.displayOptionsPanel
  );
  const extendWithHint = useFeatureHintTarget(MONITORING_HINT_ID.extendWith);

  const isPanelOpen = isOpen || isDisplayOptionsHintActive;

  useEffect(() => {
    if (!isDisplayOptionsHintActive) {
      handleClose();
    }
  }, [isDisplayOptionsHintActive, handleClose]);

  useClickOutside(panelRef, handleClose, isOpen);

  const enteringClassOptions: SelectOption<number>[] =
    filterOptions.enteringClasses.map((year) => ({
      label: String(year),
      value: year,
    }));
  const cohortOptions: SelectOption<string>[] = filterOptions.cohorts.map(
    (cohort) => ({ label: cohort.label, value: cohort.key })
  );

  return (
    <Stack
      direction="row"
      sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1, py: 1 }}
    >
      <span className="text-sm font-semibold text-gray-500">Extend with</span>
      {!!extendOptionsError && (
        <span className="flex items-center gap-2 text-xs text-critical-600">
          Couldn&apos;t load Extend With options.{' '}
          <button
            type="button"
            className="underline font-semibold"
            onClick={onRetryExtendOptions}
          >
            Retry
          </button>
        </span>
      )}
      <div
        {...extendWithHint}
        className={classNames(
          'flex flex-wrap items-center gap-2',
          extendWithHint.className
        )}
      >
        {ALL_EXTEND_WITH_OPTIONS.map((option) => {
          const isActive = params.extend.includes(option);

          return (
            <button
              key={option}
              type="button"
              className={classNames(
                CHIP_BASE,
                CHIP_SIZE,
                isActive ? CHIP_ACTIVE : CHIP_INACTIVE
              )}
              onClick={() => onExtendToggle(option)}
              aria-pressed={isActive}
              data-testid={`extend-with-${option}`}
            >
              {EXTEND_WITH_PRODUCT_LABEL[option]}
            </button>
          );
        })}
      </div>

      <Box ref={panelRef} sx={{ position: 'relative', ml: 'auto' }}>
        <Button
          {...displayOptionsButtonHint}
          variant="reserve-outlined"
          size="sm"
          className={classNames(
            'w-[140px]',
            displayOptionsButtonHint.className
          )}
          iconAfter={
            <Icons.ChevronDown
              className={classNames('transition-transform', {
                'rotate-180': isPanelOpen,
              })}
              aria-hidden="true"
            />
          }
          onClick={handleToggle}
          aria-expanded={isPanelOpen}
          data-testid="filters-display-options-toggle"
        >
          Display options
        </Button>

        {isPanelOpen && (
          <fieldset
            {...displayOptionsPanelHint}
            className={classNames(
              'absolute right-0 top-full z-50 mt-2 w-[403px] rounded-[10px] border border-navy-200 bg-white p-[21px] shadow',
              displayOptionsPanelHint.className
            )}
          >
            <legend className="sr-only">Display options</legend>
            <Stack sx={{ gap: 1.5 }}>
              <Select<SelectOption<number>>
                label="Entering class"
                labelInTop
                customClass={FIELD_LABEL_CLASS}
                selectWrapperClass="w-full"
                isSearchable={false}
                options={enteringClassOptions}
                value={enteringClassOptions.find(
                  (option) => option.value === params.enteringClass
                )}
                onChange={(value) => {
                  if (value && !Array.isArray(value)) {
                    onEnteringClassChange(value.value);
                  }
                }}
              />

              <Select<SelectOption<CompareTo>>
                label="Change in time"
                labelInTop
                customClass={FIELD_LABEL_CLASS}
                selectWrapperClass="w-full"
                isSearchable={false}
                options={COMPARE_TO_OPTIONS}
                value={COMPARE_TO_OPTIONS.find(
                  (option) => option.value === params.compareTo
                )}
                onChange={(value) => {
                  if (value && !Array.isArray(value)) {
                    onCompareToChange(value.value);
                  }
                }}
              />

              <Select<SelectOption<string>>
                label="Cohort"
                labelInTop
                customClass={FIELD_LABEL_CLASS}
                selectWrapperClass="w-full"
                isSearchable={false}
                options={cohortOptions}
                value={cohortOptions.find(
                  (option) => option.value === params.cohort
                )}
                onChange={(value) => {
                  if (value && !Array.isArray(value)) {
                    onCohortChange(value.value);
                  }
                }}
              />
            </Stack>

            <Box sx={{ mt: 1.25, pt: 1.25 }}>
              <Box
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  fontWeight: 600,
                  color: 'tokens.gray.700',
                  mb: 1,
                }}
              >
                Compare to
              </Box>
              <Stack
                direction="row"
                sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
              >
                <button
                  type="button"
                  className={classNames(
                    CHIP_BASE,
                    CHIP_SIZE,
                    params.show.client ? CHIP_ACTIVE : CHIP_INACTIVE
                  )}
                  onClick={() => onShowToggle('client', !params.show.client)}
                  aria-pressed={params.show.client}
                  data-testid="show-toggle-client"
                >
                  {SHOW_TOGGLE_LABEL.client}
                </button>
                <button
                  type="button"
                  className={classNames(
                    CHIP_BASE,
                    CHIP_SIZE,
                    params.show.cohort ? CHIP_ACTIVE : CHIP_INACTIVE
                  )}
                  onClick={() => onShowToggle('cohort', !params.show.cohort)}
                  aria-pressed={params.show.cohort}
                  data-testid="show-toggle-cohort"
                >
                  {SHOW_TOGGLE_LABEL.cohort}
                </button>

                {params.show.client && params.show.cohort && (
                  <>
                    <span className="h-4 w-px bg-gray-200" aria-hidden="true" />
                    <button
                      type="button"
                      className={classNames(
                        CHIP_BASE,
                        CHIP_SIZE,
                        params.show.vsCohort ? CHIP_ACTIVE : CHIP_INACTIVE
                      )}
                      onClick={() =>
                        onShowToggle('vsCohort', !params.show.vsCohort)
                      }
                      aria-pressed={params.show.vsCohort}
                      data-testid="show-toggle-vsCohort"
                    >
                      {SHOW_TOGGLE_LABEL.vsCohort}
                    </button>
                  </>
                )}
              </Stack>
            </Box>
          </fieldset>
        )}
      </Box>
    </Stack>
  );
};

export default FiltersBar;
